import React, { useState } from 'react';
import {
  FileText,
  CheckCircle,
  Clock,
  Banknote,
  AlertCircle,
  Book,
  Plane,
  User,
  ChevronDown,
  ChevronUp,
  Radio,
  Wrench,
  Users,
  Headphones,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { LICENSE_CATEGORY_LABELS, APP_NAME } from '@utils/constants';

type LicenseType = 'PILOT' | 'CABIN_CREW' | 'FLIGHT_DISPATCHER' | 'AME' | 'ATC' | 'ATSEP' | 'ASO';

export const Requirements: React.FC = () => {
  const [expandedLicense, setExpandedLicense] = useState<LicenseType | null>('PILOT');

  const licenses = [
    {
      id: 'PILOT' as const,
      name: LICENSE_CATEGORY_LABELS.PILOT,
      description: 'For professional and recreational pilots operating aircraft in Nigerian airspace',
      color: 'blue',
      icon: <Plane className="h-6 w-6" />,
      requirements: {
        'Minimum Age': '17 years (PPL), 18 years (CPL), 21 years (ATPL)',
        'Medical Certificate': 'Class 1 or Class 2 from NCAA-approved AME',
        'Flight Experience': 'Varies by license type (40 hours for PPL, 200+ for CPL)',
        'Theory Knowledge': 'Pass all required ground school examinations',
        'Practical Test': 'Pass flight proficiency check with approved examiner',
      },
      documents: [
        'NIN Slip (National Identity Number)',
        'Birth Certificate or Age Declaration',
        'Passport photographs (4 copies)',
        'Educational certificates (minimum SSCE with Maths, English, Physics)',
        'Medical fitness certificate',
        'Logbook (for renewal)',
        'Proficiency check certificate',
        'Aircraft ratings documentation',
      ],
      duration: '6-24 months (depending on license type)',
      cost: '₦2,500,000 - ₦25,000,000 (varies by license level)',
    },
    {
      id: 'CABIN_CREW' as const,
      name: LICENSE_CATEGORY_LABELS.CABIN_CREW,
      description: 'For cabin crew members ensuring passenger safety and comfort on commercial flights',
      color: 'pink',
      icon: <Users className="h-6 w-6" />,
      requirements: {
        'Minimum Age': '18 years',
        'Medical Certificate': 'Class 2 or Class 3',
        'Height Requirement': 'Minimum reach of 212cm (arm reach test)',
        'Training': 'Complete approved cabin crew training course',
        'Emergency Procedures': 'Pass emergency drills and safety procedures',
      },
      documents: [
        'NIN Slip',
        'Birth Certificate',
        'Passport photographs',
        'Educational certificates (minimum SSCE)',
        'Medical fitness certificate',
        'Cabin crew training certificate',
        'Emergency drills completion certificates',
        'Recurrent training records (for renewal)',
      ],
      duration: '3-6 months initial training',
      cost: '₦500,000 - ₦1,500,000',
    },
    {
      id: 'FLIGHT_DISPATCHER' as const,
      name: LICENSE_CATEGORY_LABELS.FLIGHT_DISPATCHER,
      description: 'For flight operations officers responsible for flight planning and operations',
      color: 'indigo',
      icon: <FileText className="h-6 w-6" />,
      requirements: {
        'Minimum Age': '21 years',
        'Education': 'Minimum of secondary school certificate',
        'Training': 'Complete NCAA-approved flight dispatcher course',
        'Knowledge Test': 'Pass flight dispatcher written examinations',
        'Practical Experience': 'Demonstrate flight planning proficiency',
      },
      documents: [
        'NIN Slip',
        'Birth Certificate',
        'Passport photographs',
        'Educational certificates',
        'Flight dispatcher training certificate',
        'Recurrency training certificate (for renewal)',
        'Competency check certificate',
      ],
      duration: '6-12 months',
      cost: '₦800,000 - ₦2,000,000',
    },
    {
      id: 'AME' as const,
      name: LICENSE_CATEGORY_LABELS.AME,
      description: 'For aircraft maintenance engineers authorized to certify aircraft airworthiness',
      color: 'orange',
      icon: <Wrench className="h-6 w-6" />,
      requirements: {
        'Minimum Age': '21 years',
        'Education': 'Technical/Engineering qualification',
        'Training': 'Complete approved AME training program',
        'Categories': 'A (Aircraft), B1 (Mechanical), B2 (Avionics), C (Base Maintenance)',
        'Experience': 'Practical maintenance experience required',
      },
      documents: [
        'NIN Slip',
        'Birth Certificate',
        'Passport photographs',
        'Engineering certificates/diplomas',
        'AME training certificates',
        'Category and type ratings documentation',
        'Logbook showing maintenance experience',
        'Competency certificates (for renewal)',
      ],
      duration: '2-4 years (depending on category)',
      cost: '₦3,000,000 - ₦8,000,000',
    },
    {
      id: 'ATC' as const,
      name: LICENSE_CATEGORY_LABELS.ATC,
      description: 'For air traffic controllers managing aircraft movements and ensuring safe separation',
      color: 'green',
      icon: <Radio className="h-6 w-6" />,
      requirements: {
        'Minimum Age': '21 years',
        'Education': 'University degree or equivalent',
        'Medical': 'Pass ATC medical examination',
        'Training': 'Complete ATC training at approved facility',
        'Ratings': 'Tower Control, Approach Control, Area Control',
        'English Proficiency': 'ICAO Level 4 or higher',
      },
      documents: [
        'NIN Slip',
        'Birth Certificate',
        'Passport photographs',
        'University degree certificate',
        'ATC training certificates',
        'Medical certificate',
        'Rating endorsements',
        'Proficiency check records (for renewal)',
        'English language proficiency certificate',
      ],
      duration: '18-24 months',
      cost: '₦5,000,000 - ₦10,000,000',
    },
    {
      id: 'ATSEP' as const,
      name: LICENSE_CATEGORY_LABELS.ATSEP,
      description: 'For personnel maintaining air traffic services equipment and systems',
      color: 'teal',
      icon: <Wrench className="h-6 w-6" />,
      requirements: {
        'Minimum Age': '21 years',
        'Education': 'Engineering degree or technical diploma',
        'Training': 'Complete ATSEP training program',
        'Categories': 'Communication, Navigation, Surveillance, Data Processing',
        'Competency': 'Demonstrate technical proficiency',
      },
      documents: [
        'NIN Slip',
        'Birth Certificate',
        'Passport photographs',
        'Engineering/technical certificates',
        'ATSEP training certificates',
        'Category endorsements (A/B/C/D)',
        'Competency certificates',
        'Maintenance records (for renewal)',
      ],
      duration: '12-24 months',
      cost: '₦2,500,000 - ₦6,000,000',
    },
    {
      id: 'ASO' as const,
      name: LICENSE_CATEGORY_LABELS.ASO,
      description: 'For aeronautical station operators handling aviation radio communications',
      color: 'purple',
      icon: <Headphones className="h-6 w-6" />,
      requirements: {
        'Minimum Age': '18 years',
        'Education': 'Secondary school certificate',
        'Training': 'Complete aeronautical communications training',
        'Radio Telephony': 'Pass radio telephony procedures examination',
        'English': 'Good command of English language',
      },
      documents: [
        'NIN Slip',
        'Birth Certificate',
        'Passport photographs',
        'Educational certificates',
        'Radio communications training certificate',
        'Competency certificate',
        'English proficiency documentation',
      ],
      duration: '3-6 months',
      cost: '₦400,000 - ₦1,000,000',
    },
  ];

  const generalRequirements = [
    {
      icon: <User className="h-6 w-6 text-blue-600" />,
      title: 'NIN Verification',
      description: 'Valid National Identity Number (NIN) is mandatory for all applicants',
    },
    {
      icon: <Book className="h-6 w-6 text-green-600" />,
      title: 'Educational Qualification',
      description: 'Minimum education varies by license type - from SSCE to university degree',
    },
    {
      icon: <FileText className="h-6 w-6 text-purple-600" />,
      title: 'Medical Fitness',
      description: 'Valid medical certificate from NCAA-approved Aviation Medical Examiner',
    },
    {
      icon: <Clock className="h-6 w-6 text-orange-600" />,
      title: 'Training & Certification',
      description: 'Complete all required training from NCAA-approved organizations',
    },
  ];

  const applicationProcess = [
    { step: 1, title: 'Create Account', description: `Register on ${APP_NAME}` },
    { step: 2, title: 'Select License Type', description: 'Choose NEW license or RENEWAL application' },
    { step: 3, title: 'Complete Application', description: 'Fill in all required information' },
    { step: 4, title: 'Upload Documents', description: 'Upload all required documents (PDF for existing licenses)' },
    { step: 5, title: 'Book Appointment', description: 'Schedule biometric capture at NCAA regional office' },
    { step: 6, title: 'Biometric Capture', description: 'Visit NCAA center for fingerprints and photo' },
    { step: 7, title: 'Document Review', description: 'NCAA reviews application and documents' },
    { step: 8, title: 'License Issuance', description: 'Receive your aviation license' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-ncaa-primary to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">License Requirements</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Complete guide to NCAA license requirements for all aviation personnel in Nigeria
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* General Requirements */}
        <Card className="mb-8">
          <h2 className="text-3xl font-bold text-ncaa-dark mb-6">General Requirements</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {generalRequirements.map((req, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  {req.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{req.title}</h3>
                <p className="text-sm text-gray-600">{req.description}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* License Types */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-ncaa-dark mb-6">License Types & Requirements</h2>
          <div className="space-y-4">
            {licenses.map((license) => (
              <Card key={license.id} className="overflow-hidden">
                <button
                  onClick={() => setExpandedLicense(expandedLicense === license.id ? null : license.id)}
                  className="w-full"
                >
                  <div className="flex items-center justify-between p-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 bg-${license.color}-100 rounded-lg flex items-center justify-center`}>
                        {license.icon}
                      </div>
                      <div className="text-left">
                        <h3 className="text-xl font-bold text-gray-900">{license.name}</h3>
                        <p className="text-gray-600">{license.description}</p>
                      </div>
                    </div>
                    {expandedLicense === license.id ? (
                      <ChevronUp className="h-6 w-6 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                </button>

                {expandedLicense === license.id && (
                  <div className="px-6 pb-6 border-t border-gray-200 pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      {/* Key Requirements */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Key Requirements</h4>
                        <div className="space-y-2">
                          {Object.entries(license.requirements).map(([key, value]) => (
                            <div key={key} className="flex items-start gap-2">
                              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <span className="font-medium text-gray-700 capitalize">{key}:</span>
                                <span className="text-gray-600 ml-1">{value}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Additional Information</h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-blue-600" />
                            <span className="text-gray-600">
                              <strong>Duration:</strong> {license.duration}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Banknote className="h-5 w-5 text-green-600" />
                            <span className="text-gray-600">
                              <strong>Estimated Cost:</strong> {license.cost}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Required Documents */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Required Documents</h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {license.documents.map((doc, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
                            <span className="text-sm text-gray-600">{doc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* Application Process */}
        <Card className="mb-8">
          <h2 className="text-3xl font-bold text-ncaa-dark mb-6">Application Process</h2>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block" />

            <div className="space-y-6">
              {applicationProcess.map((item) => (
                <div key={item.step} className="relative flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-ncaa-primary text-white rounded-full flex items-center justify-center font-bold relative z-10">
                      {item.step}
                    </div>
                  </div>
                  <div className="flex-1 pb-6">
                    <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Important Notes */}
        <Card className="bg-yellow-50 border-yellow-200 mb-8">
          <div className="flex gap-4">
            <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Important Notes</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• All costs are estimates and may vary depending on the training organization</li>
                <li>• Existing license copies must be submitted in PDF format only for renewals</li>
                <li>• Medical certificates must remain valid throughout the licensing process</li>
                <li>• All training must be conducted by NCAA-approved organizations</li>
                <li>• Licenses are valid for specific periods and require renewal</li>
                <li>• Enrolment centers: NCAA Abuja, Lagos, Kano, and Port Harcourt offices</li>
                <li>• Both NEW and RENEWAL applications can be processed through {APP_NAME}</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-ncaa-primary to-blue-600 text-white">
          <div className="text-center py-8">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Your Journey?</h2>
            <p className="text-xl text-blue-100 mb-6">
              Create your account today and begin your aviation career
            </p>
            <a
              href="/register"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-ncaa-primary font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Create Account
            </a>
          </div>
        </Card>
      </div>
    </div>
  );
};
